import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Coins, Scale, ScrollText, Store, BookOpen, Landmark,
  Heart, Play, Sparkles, ShieldCheck, Building2, ChevronRight, Languages, Mail,
  FolderArchive,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useZakatGlobal } from '../context/ZakatGlobalContext';
import { HeaderShareButton } from './HeaderShareButton';
import { TextSizeToggle } from './TextSizeToggle';

/**
 * Single source of truth for the mobile navigation menu used on
 * EVERY page of the app — Home, Inheritance, Will, Life, Learn, Legal,
 * Privacy, Women's Rights and the Masjid Resources hub.
 *
 * Why: the previous implementation had three slightly-different copies
 * (home, inheritance, will). The shape, link order and styling drifted
 * which made the menu feel unstable as users navigated.
 *
 * The menu always renders the same two groups in the same order:
 *   1. "Calculators" — Zakat / Inheritance / Will / Business Zakat
 *   2. "Life & references" — Life / Learn / Legal / Women / Walkthrough /
 *      Masjid / Privacy
 *
 * Optional 3rd group:
 *   3. `onPageLinks` — an array of `{ label, testid, action }` rendered as
 *      a "On this page" group (used only on the homepage for scroll-anchors
 *      like FAQ / Rulings).
 *
 * The currently-active page is auto-detected from `useLocation()` and
 * rendered as a disabled `aria-current` item.
 */
const MobileNavLink = ({ icon: Icon, label, onClick, testid, tone, current }) => (
  <button
    type="button"
    onClick={current ? undefined : onClick}
    data-testid={testid}
    aria-current={current ? 'page' : undefined}
    aria-disabled={current ? 'true' : undefined}
    disabled={current}
    className={`w-full flex items-center gap-2.5 py-2 pl-3 pr-2 -mx-2 rounded-md text-left text-[15px] font-medium transition-colors ${
      current
        ? 'text-[#0D1520] bg-[#F3EFEA] border-l-[3px] border-[#0D4733] cursor-default'
        : tone === 'gold'
          ? 'text-[#0D4733] hover:bg-[#F3EFEA]'
          : 'text-[#0D1520] hover:bg-[#F3EFEA]'
    }`}
  >
    {Icon && <Icon className={`w-4 h-4 shrink-0 ${current || tone === 'gold' ? 'text-[#0D4733]' : 'text-[#0D4733]'}`} />}
    <span className="flex-1">{label}</span>
    {!current && <ChevronRight className="w-3.5 h-3.5 text-[#8B8172]" />}
  </button>
);

export const AppMobileMenu = ({
  onClose,
  // Optional language picker — when both `languages` and `selectedLanguage`
  // are provided, the menu shows an inline language switcher at the top.
  languages,
  selectedLanguage,
  updateLanguage,
  // Optional country picker — pass a CountryPicker element (already wired
  // up with its own state) to render it at the top of the menu.
  countryPickerNode,
  // Optional "On this page" extras: [{ label, testid, action, icon }]
  onPageLinks = [],
  // Override label for the Zakat link when on the homepage (which scrolls
  // to the calculator section instead of navigating). Pass `null` to
  // disable the override (default behaviour = navigate to '/').
  zakatScrollHandler = null,
  // Test id prefix so multiple instances don't collide.
  testidPrefix = 'mobile-nav',
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useZakatGlobal();

  const go = (path) => { navigate(path); onClose?.(); };
  const scroll = (handler) => { handler?.(); onClose?.(); };

  // Determine which item should be highlighted as current.
  const isHome  = pathname === '/' || pathname === '';
  const isInh   = pathname.startsWith('/inheritance');
  const isWill  = pathname.startsWith('/will');
  const isSucc  = pathname.startsWith('/succession');
  const isBiz   = pathname.startsWith('/business-calculator');
  const isLife  = pathname.startsWith('/life');
  const isLearn = pathname.startsWith('/learn');
  const isLegal = pathname.startsWith('/legal');
  const isWom   = pathname.startsWith('/womens-rights');
  const isWalk  = pathname.startsWith('/walkthrough');
  const isMasjid = pathname.startsWith('/masjid-resources');
  const isPrivacy = pathname.startsWith('/privacy');
  const isContact = pathname.startsWith('/contact');

  return (
    <div
      data-testid={`${testidPrefix}-panel`}
      className="px-4 py-2.5 space-y-2.5"
    >
      {/* Top: optional country + language quick switchers */}
      {(countryPickerNode || (languages && selectedLanguage)) && (
        <div className="flex items-center gap-2">
          {countryPickerNode && (
            <>
              <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#4A5568] shrink-0">{t('v2.menu.section.country')}</div>
              <div className="flex-1" data-testid={`${testidPrefix}-country-wrap`}>
                {countryPickerNode}
              </div>
            </>
          )}
          {languages && selectedLanguage && (
            <Select value={selectedLanguage.code} onValueChange={updateLanguage}>
              <SelectTrigger data-testid={`${testidPrefix}-language`} className="h-9 w-[110px] bg-white border border-[#CBD2D9] text-[#0D1520] hover:bg-[#F3EFEA] hover:border-[#0D4733] focus:ring-2 focus:ring-[#0D4733]/40">
                <Languages className="w-3.5 h-3.5 mr-1.5 text-[#0D4733]" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>{lang.nameNative}{lang.beta && <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#0D4733]">Beta</span>}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Share + Accessibility toggle row — surfaced here so the mobile
          top bar stays compact enough for narrow devices. */}
      <div
        className="flex items-center gap-2 pt-2 border-t border-[#E2DCD5]"
        data-testid={`${testidPrefix}-utility-row`}
      >
        <HeaderShareButton testid={`${testidPrefix}-share`} />
        <TextSizeToggle testid={`${testidPrefix}-text-size`} />
      </div>

      {/* Calculators group */}
      <div className={countryPickerNode || (languages && selectedLanguage) ? 'pt-2 border-t border-[#E2DCD5]' : ''}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1.5">{t('v2.menu.section.calculators')}</p>
        <MobileNavLink
          testid={`${testidPrefix}-zakat`}
          onClick={isHome && zakatScrollHandler ? () => scroll(zakatScrollHandler) : () => go('/')}
          icon={Coins}
          label={t('v2.menu.item.zakat')}
          tone="gold"
          current={isHome}
        />
        <MobileNavLink testid={`${testidPrefix}-inheritance`} onClick={() => go('/inheritance')} icon={Scale}      label={t('v2.menu.item.inheritance')} tone="gold" current={isInh} />
        <MobileNavLink testid={`${testidPrefix}-will`}        onClick={() => go('/will')}        icon={ScrollText} label={t('v2.menu.item.will')}        tone="gold" current={isWill} />
        <MobileNavLink testid={`${testidPrefix}-succession`}  onClick={() => go('/succession')}  icon={FolderArchive} label={t('v2.menu.item.succession')} tone="gold" current={isSucc} />
        <MobileNavLink testid={`${testidPrefix}-business`}    onClick={() => go('/business-calculator')} icon={Store} label={t('v2.menu.item.business')} tone="gold" current={isBiz} />
      </div>

      {/* Life & references group */}
      <div className="pt-2 border-t border-[#E2DCD5]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1.5">{t('v2.menu.section.lifeRefs')}</p>
        <MobileNavLink testid={`${testidPrefix}-life`}          onClick={() => go('/life')}             icon={Sparkles}    label={t('v2.menu.item.life')}         current={isLife} />
        <MobileNavLink testid={`${testidPrefix}-learn`}         onClick={() => go('/learn')}            icon={BookOpen}    label={t('v2.menu.item.learn')}        current={isLearn} />
        <MobileNavLink testid={`${testidPrefix}-legal`}         onClick={() => go('/legal')}            icon={Landmark}    label={t('v2.menu.item.legal')}        current={isLegal} />
        <MobileNavLink testid={`${testidPrefix}-womens-rights`} onClick={() => go('/womens-rights')}    icon={Heart}       label={t('v2.menu.item.women')}        current={isWom} />
        <MobileNavLink testid={`${testidPrefix}-walkthrough`}   onClick={() => go('/walkthrough')}      icon={Play}        label={t('v2.menu.item.walkthrough')}  current={isWalk} />
        <MobileNavLink testid={`${testidPrefix}-masjid`}        onClick={() => go('/masjid-resources')} icon={Building2}   label={t('v2.menu.item.masjid')}       current={isMasjid} />
        <MobileNavLink testid={`${testidPrefix}-contact`}       onClick={() => go('/contact')}          icon={Mail}        label={t('v2.menu.item.contact')}      current={isContact} />
        <MobileNavLink testid={`${testidPrefix}-privacy`}       onClick={() => go('/privacy')}          icon={ShieldCheck} label={t('v2.menu.item.privacy')}      current={isPrivacy} />
      </div>

      {/* Optional: "On this page" scroll-anchors */}
      {onPageLinks.length > 0 && (
        <div className="pt-2 border-t border-[#E2DCD5]">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1.5">{t('v2.menu.section.onPage')}</p>
          {onPageLinks.map((l) => (
            <MobileNavLink
              key={l.testid || l.label}
              testid={l.testid}
              icon={l.icon}
              label={l.label}
              onClick={() => scroll(l.action)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AppMobileMenu;
